/**
 * Public CAT Profile Page
 *
 * Read-only view of a shared CAT Profile.
 * No authentication required  -  accessible via share link.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { catProfileService } from '../services/catProfileService';
import { SEOHead } from '../components/SEOHead';
import {
  GraduationCap,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Award,
  BarChart3,
} from 'lucide-react';

export default function PublicCATProfilePage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!shareToken) return;
    (async () => {
      try {
        const data = await catProfileService.getPublicProfile(shareToken);
        if (!data) {
          setError('Hồ sơ không tồn tại hoặc đã tắt chia sẻ.');
        } else {
          setProfile(data);
        }
      } catch {
        setError('Không thể tải hồ sơ.');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken]);

  const toggleGroup = (groupCode: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupCode]: !prev[groupCode] }));
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-center space-y-3'>
          <Loader2 className='w-10 h-10 animate-spin text-primary mx-auto' />
          <p className='text-gray-500'>Đang tải hồ sơ...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
        <div className='text-center space-y-4 max-w-md'>
          <AlertCircle className='w-16 h-16 text-gray-300 mx-auto' />
          <h1 className='text-xl font-bold text-gray-800'>{error || 'Không tìm thấy hồ sơ'}</h1>
          <p className='text-gray-500'>
            Link chia sẻ có thể đã hết hạn hoặc bị tắt bởi chủ sở hữu.
          </p>
          <Link
            to='/'
            className='inline-block px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors'
          >
            Về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  const dateStr = new Date(profile.generatedAt).toLocaleDateString('vi-VN');
  const caseLabel = profile.caseType === 'CASE_A' ? 'Trung bình 3 môn' : 'Trung bình 1 môn (3 lần)';

  // Calculate overall average
  const overallAverage =
    profile.lowScoreWarning?.overallAverage ||
    (profile.examScores.length > 0
      ? Math.round(
          (profile.examScores.reduce((sum: number, s: any) => sum + (s.average || 0), 0) /
            profile.examScores.length) *
            10
        ) / 10
      : 0);

  const groupColors: Record<string, string> = {
    group1: '#0369A1',
    group2: '#F97316',
    group3: '#DC2626',
    group4: '#16A34A',
    group5: '#9333EA',
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <SEOHead
        title={`Hồ sơ năng lực CAT | Hướng nghiệp CAT`}
        description='Xem hồ sơ năng lực CAT - Kết quả đánh giá và định hướng nghề nghiệp.'
        noindex
      />
      {/* Header */}
      <header className='bg-white border-b border-gray-200 px-4 py-2'>
        <div className='max-w-5xl mx-auto flex items-center justify-between'>
          <Link to='/' className='flex items-center gap-3'>
            <div className='w-10 h-10 flex items-center justify-center overflow-hidden'>
              <img
                alt='Logo Viện'
                className='w-full h-full object-contain'
                src='/logo-dinh-huong-nghe-nghiep.png'
              />
            </div>
            <div className='flex flex-col justify-center'>
              <span className='font-bold text-xs text-[#0369A1] uppercase tracking-tight'>
                Hướng Nghiệp CAT
              </span>
              <span className='text-[10px] leading-none text-[#64748B]'>
                Viện Khảo thí &amp; ĐGCLGD
              </span>
            </div>
          </Link>
          <Link to='/ho-so-nang-luc' className='text-sm font-semibold text-primary hover:underline'>
            Tạo hồ sơ của bạn →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className='bg-gradient-to-br from-[#D4841C] via-[#E8A84C] to-[#F5C97A] text-white'>
        <div className='max-w-5xl mx-auto px-4 py-8'>
          <p className='text-sm font-semibold opacity-80 uppercase tracking-wider mb-1'>
            Hồ sơ năng lực
          </p>
          <h1 className='text-2xl sm:text-3xl font-bold mb-1'>{profile.userName}</h1>
          <p className='text-sm opacity-80'>
            {profile.userClass} • Tạo ngày {dateStr}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className='max-w-5xl mx-auto px-4 py-8 space-y-6'>
        {/* Summary Cards */}
        <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
          <div className='bg-white rounded-xl border border-gray-200 p-4 text-center'>
            <div
              className='text-2xl font-bold'
              style={{
                color:
                  overallAverage >= 7 ? '#16A34A' : overallAverage >= 5 ? '#0369A1' : '#F97316',
              }}
            >
              {overallAverage.toFixed(1)}
            </div>
            <div className='text-xs text-gray-500 font-semibold mt-1'>ĐIỂM TB</div>
          </div>
          <div className='bg-white rounded-xl border border-gray-200 p-4 text-center'>
            <div className='text-2xl font-bold text-sky-700'>{profile.totalRecommendedMajors}</div>
            <div className='text-xs text-gray-500 font-semibold mt-1'>NGÀNH PHÙ HỢP</div>
          </div>
          <div className='bg-white rounded-xl border border-gray-200 p-4 text-center'>
            <div className='text-2xl font-bold text-green-600'>{profile.totalPassedMajors}</div>
            <div className='text-xs text-gray-500 font-semibold mt-1'>NGÀNH ĐẠT</div>
          </div>
          <div className='bg-white rounded-xl border border-gray-200 p-4 text-center'>
            <div className='text-2xl font-bold text-orange-500'>
              {profile.totalRecommendedMajors - profile.totalPassedMajors}
            </div>
            <div className='text-xs text-gray-500 font-semibold mt-1'>GẦN ĐẠT</div>
          </div>
        </div>

        {/* Exam Scores */}
        <div className='bg-white rounded-xl border border-gray-200 p-5'>
          <div className='flex items-center gap-2 mb-4'>
            <BarChart3 className='w-5 h-5 text-primary' />
            <h2 className='text-base font-bold text-gray-900'>Bảng điểm luyện đề</h2>
            <span className='ml-auto text-xs font-semibold bg-gray-100 px-2.5 py-0.5 rounded-full text-gray-600'>
              {profile.examScores.length} môn
            </span>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-200'>
                  <th className='text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase'>
                    Môn thi
                  </th>
                  <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase'>
                    Số lần
                  </th>
                  <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase'>
                    Điểm từng lần
                  </th>
                  <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase'>
                    Trung bình
                  </th>
                </tr>
              </thead>
              <tbody>
                {profile.examScores.map((s: any, i: number) => (
                  <tr key={i} className='border-b border-gray-100'>
                    <td className='py-2.5 px-3 font-semibold text-gray-800'>
                      {s.subjectName || s.subject}
                    </td>
                    <td className='py-2.5 px-3 text-center text-gray-600'>{s.attemptCount}</td>
                    <td className='py-2.5 px-3 text-center text-gray-600'>
                      {s.scores?.map((sc: number) => sc.toFixed(1)).join(', ') || '-'}
                    </td>
                    <td
                      className='py-2.5 px-3 text-center font-bold'
                      style={{
                        color: s.average >= 7 ? '#16A34A' : s.average >= 5 ? '#F97316' : '#DC2626',
                      }}
                    >
                      {s.average?.toFixed(1) || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommendations */}
        <div className='bg-white rounded-xl border border-gray-200 p-5'>
          <div className='flex items-center gap-2 mb-4'>
            <Award className='w-5 h-5 text-primary' />
            <h2 className='text-base font-bold text-gray-900'>Ngành học phù hợp</h2>
          </div>

          <div className='space-y-3'>
            {profile.recommendations.map((group: any) => {
              const isExpanded = expandedGroups[group.groupCode] !== false;
              const color = groupColors[group.groupCode] || '#0369A1';

              return (
                <div
                  key={group.groupCode}
                  className='border border-gray-200 rounded-xl overflow-hidden'
                >
                  <button
                    onClick={() => toggleGroup(group.groupCode)}
                    className='w-full flex items-center justify-between p-3.5 hover:bg-gray-50 transition-colors'
                  >
                    <div className='flex items-center gap-2'>
                      <div className='w-3 h-3 rounded-full' style={{ backgroundColor: color }} />
                      <span className='font-bold text-sm' style={{ color }}>
                        {group.groupName}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='text-xs text-gray-500 font-semibold'>
                        {group.passedMajors}/{group.totalMajors} đạt
                      </span>
                      {isExpanded ? (
                        <ChevronUp className='w-4 h-4 text-gray-400' />
                      ) : (
                        <ChevronDown className='w-4 h-4 text-gray-400' />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className='border-t border-gray-100'>
                      <table className='w-full text-sm'>
                        <thead>
                          <tr className='bg-gray-50'>
                            <th className='text-left py-2 px-3 text-xs font-semibold text-gray-500'>
                              Ngành
                            </th>
                            <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500'>
                              Môn
                            </th>
                            <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500'>
                              Ngưỡng
                            </th>
                            <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500'>
                              Điểm
                            </th>
                            <th className='text-center py-2 px-3 text-xs font-semibold text-gray-500'>
                              Kết quả
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.majors.map((major: any, j: number) => {
                            const gap = major.threshold - major.userScore;
                            return (
                              <tr
                                key={j}
                                className={`border-b border-gray-50 ${!major.passed ? 'opacity-70' : ''}`}
                              >
                                <td className='py-2 px-3 text-gray-800'>{major.majorName}</td>
                                <td className='py-2 px-3 text-center text-gray-600 text-xs'>
                                  {major.primarySubjectName}
                                </td>
                                <td className='py-2 px-3 text-center text-gray-600'>
                                  {major.threshold.toFixed(1)}
                                </td>
                                <td
                                  className='py-2 px-3 text-center font-bold'
                                  style={{ color: major.passed ? '#16A34A' : '#DC2626' }}
                                >
                                  {major.userScore.toFixed(1)}
                                </td>
                                <td className='py-2 px-3 text-center'>
                                  {major.passed ? (
                                    <span className='text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full'>
                                      ĐẠT
                                    </span>
                                  ) : (
                                    <span className='text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full'>
                                      Thiếu {gap.toFixed(1)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className='text-center py-6'>
          <p className='text-sm text-gray-500 mb-3'>Bạn cũng muốn biết ngành học phù hợp?</p>
          <Link
            to='/'
            className='inline-block px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors shadow-md'
          >
            Tạo hồ sơ năng lực của bạn
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className='border-t border-gray-200 bg-white py-4 text-center text-xs text-gray-400'>
        © {new Date().getFullYear()} Hướng Nghiệp CAT - IEQTE & Cognotiv Việt Nam
      </footer>
    </div>
  );
}
